import React, { useState, useRef, useEffect } from 'react'

interface StatBarProps {
  /** Label for the stat */
  label: string
  /** Current value */
  value: number
  /** Maximum value for percentage calculation */
  max?: number
  /** Format for displaying value (default shows raw number) */
  format?: 'number' | 'percent' | 'decimal'
  /** Number of decimal places for decimal format */
  decimals?: number
  /** Custom color for the bar fill */
  color?: string
  /** Tooltip text shown on hover (optional) */
  tooltip?: string
  /** Additional context shown in tooltip (e.g., comparison value) */
  tooltipContext?: string
}

/**
 * Single stat bar with label, progress track, and value display (FotMob style).
 * Supports optional tooltips on hover.
 */
export function StatBar({
  label,
  value,
  max = 100,
  format = 'number',
  decimals = 1,
  color,
  tooltip,
  tooltipContext,
}: StatBarProps) {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
  const barRef = useRef<HTMLDivElement>(null)
  const percentage = Math.min((value / max) * 100, 100)

  const displayValue = formatValue(value, format, decimals)
  const hasTooltip = tooltip || tooltipContext

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (!hasTooltip) return
    const rect = barRef.current?.getBoundingClientRect()
    if (rect) {
      setTooltipPosition({
        x: e.clientX - rect.left,
        y: -8,
      })
    }
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!hasTooltip || !showTooltip) return
    const rect = barRef.current?.getBoundingClientRect()
    if (rect) {
      setTooltipPosition({
        x: e.clientX - rect.left,
        y: -8,
      })
    }
  }

  return (
    <div
      ref={barRef}
      className={`fm-stat-bar ${hasTooltip ? 'has-tooltip' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
    >
      <span className="fm-stat-bar-label">{label}</span>
      <div className="fm-stat-bar-track">
        <div
          className="fm-stat-bar-fill"
          style={{
            width: `${percentage}%`,
            ...(color ? { background: color } : {}),
          }}
        />
      </div>
      <span className="fm-stat-bar-value">{displayValue}</span>

      {/* Tooltip */}
      {hasTooltip && showTooltip && (
        <div
          className="fm-stat-bar-tooltip"
          style={{
            left: `${Math.min(Math.max(tooltipPosition.x, 60), (barRef.current?.offsetWidth || 200) - 60)}px`,
            top: `${tooltipPosition.y}px`,
          }}
        >
          {tooltip && <div className="fm-stat-bar-tooltip-title">{tooltip}</div>}
          {tooltipContext && <div className="fm-stat-bar-tooltip-context">{tooltipContext}</div>}
        </div>
      )}
    </div>
  )
}

function formatValue(value: number, format: string, decimals: number): string {
  switch (format) {
    case 'percent':
      return `${value.toFixed(decimals)}%`
    case 'decimal':
      return value.toFixed(decimals)
    default:
      return value.toString()
  }
}

interface ComparisonBarProps {
  /** Home team value */
  homeValue: number
  /** Away team value */
  awayValue: number
  /** Home team label (optional) */
  homeLabel?: string
  /** Away team label (optional) */
  awayLabel?: string
}

/**
 * Comparison bar showing home vs away split (FotMob style).
 */
export function ComparisonBar({
  homeValue,
  awayValue,
  homeLabel,
  awayLabel,
}: ComparisonBarProps) {
  const total = homeValue + awayValue
  const homePercent = total > 0 ? (homeValue / total) * 100 : 50
  const awayPercent = total > 0 ? (awayValue / total) * 100 : 50

  return (
    <div className="fm-comparison-wrapper">
      {(homeLabel || awayLabel) && (
        <div className="fm-comparison-labels">
          <span className="fm-comparison-label home">{homeLabel || homeValue}</span>
          <span className="fm-comparison-label away">{awayLabel || awayValue}</span>
        </div>
      )}
      <div className="fm-comparison-bar">
        <div
          className="fm-comparison-bar-home"
          style={{ width: `${homePercent}%` }}
        />
        <div
          className="fm-comparison-bar-away"
          style={{ width: `${awayPercent}%` }}
        />
      </div>
    </div>
  )
}
